import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import clientsApi from '../../api/clientsApi';
import PaginatedTable from '../../components/PaginatedTable';
import SortableTableHeader from '../../components/SortableTableHeader';

const ClientAllergies = ({ clientId, client }) => {
  const navigate = useNavigate();
  // Client data is passed as prop, no need for state
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAllergy, setSelectedAllergy] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalAllergies, setTotalAllergies] = useState(0);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch allergies data (client data is passed as prop)
      const allergiesData = await clientsApi.getClientAllergies(clientId, {
        skip: page * pageSize,
        limit: pageSize,
        order_by: sortField,
        order_direction: sortDirection
      });
      setAllergies(allergiesData.allergies || []);
      setTotalAllergies(allergiesData.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching client allergies:', err);
      setError('Failed to load client allergies. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, page, pageSize, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleBack = () => {
    // This component is embedded in the client detail page
  };

  const handleViewAllergy = (allergy) => {
    setSelectedAllergy(allergy);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAllergy(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'severe':
      case 'high':
        return 'error';
      case 'moderate':
      case 'medium':
        return 'warning';
      case 'mild':
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>

      <PaginatedTable
        title={`Allergies for ${client?.first_name} ${client?.last_name}`}
        icon={<LocalHospitalIcon color="error" sx={{ mr: 1 }} />}
        total={totalAllergies}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
      >
        {allergies.length === 0 ? (
          <Alert severity="info">No allergies found for this client.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeader
                    field="allergy_name"
                    label="Allergy Name"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="type"
                    label="Type"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="severity"
                    label="Severity"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Reaction</TableCell>
                  <SortableTableHeader
                    field="is_active"
                    label="Status"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="created_date"
                    label="Created Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allergies.map((allergy) => (
                  <TableRow key={allergy.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {allergy.allergy_name || 'Unknown Allergy'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={allergy.type || 'Unknown'} 
                        color="default"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={allergy.severity || 'Unknown'} 
                        color={getSeverityColor(allergy.severity)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {allergy.reaction || 'No reaction specified'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={allergy.is_active ? "Active" : "Inactive"} 
                        color={allergy.is_active ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(allergy.created_date)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewAllergy(allergy)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PaginatedTable>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Allergy Details</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAllergy && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedAllergy.allergy_name || 'Unknown Allergy'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {selectedAllergy.severity && (
                    <Chip 
                      label={selectedAllergy.severity} 
                      color={getSeverityColor(selectedAllergy.severity)}
                      size="small"
                    />
                  )}
                  {selectedAllergy.type && (
                    <Chip 
                      label={selectedAllergy.type} 
                      color="default"
                      size="small"
                    />
                  )}
                  <Chip 
                    label={selectedAllergy.is_active ? "Active" : "Inactive"} 
                    color={selectedAllergy.is_active ? "success" : "default"}
                    size="small"
                  />
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Allergy Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Allergy Name:</Typography>
                    <Typography variant="body1">{selectedAllergy.allergy_name || 'Unknown'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Type:</Typography>
                    <Typography variant="body1">{selectedAllergy.type || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Severity:</Typography>
                    <Typography variant="body1">{selectedAllergy.severity || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Typography variant="body1">{selectedAllergy.is_active ? 'Active' : 'Inactive'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created Date:</Typography>
                    <Typography variant="body1">{formatDate(selectedAllergy.created_date)}</Typography>
                  </Box>
                  {selectedAllergy.created_by && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Created By:</Typography>
                      <Typography variant="body1">{selectedAllergy.created_by}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              {selectedAllergy.reaction && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Reaction Details
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        backgroundColor: 'grey.50',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedAllergy.reaction}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAllergies; 